import { useState, Suspense, useEffect, useMemo, cloneElement, isValidElement } from 'react';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
/* The group-header chevrons, from admin's own set — see the note at the call
   site. Not antd's, because admin swaps two distinct glyphs and antd has no
   pair that draws them the same way. */
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
/* The collapse chevron, admin's own — its IconButton pairs ChevronLeft/Right
   with a circular 28px fill. */
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import sidebarNav from '../../config/navigation';
import PartnerHeader from './PartnerHeader';
import PartnerSpinner from '../common/PartnerSpinner';
import useAuthStore from '../../stores/useAuthStore';
import { filterNavigation } from 'helpers/navVisibility';
import { apiStatusColor } from 'constants/apiStatusBadge';
import { newChatNavState } from 'components/travelApp/newChatSignal';
import useDisableBrowserNav from 'hooks/useDisableBrowserNav';
import useThemeStore from '../../stores/useThemeStore';
import { readableTextOn } from '../../theme/brandTokens';
import { colors, palette, partnerBrand, typography, radius, space, spacing, size, borderWidth } from 'theme/tokens';

const SIDEBAR_WIDTH = size[260];
const SIDEBAR_COLLAPSED = size[72];
/**
 * THE LEFT SIDEBAR DOES NOT FOLLOW THE BRAND — product decision.
 *
 * It briefly did: the panel was derived from the brand primary and the active
 * item, its accent bar and the section icons were tinted with it. That is
 * deliberately reverted. The navigation is the operator's own furniture, the
 * same on every tenant, and repainting it per partner made the console read as
 * a different product on each login instead of one tool serving many partners.
 *
 * SO EVERY COLOUR IN THIS FILE IS A LITERAL AGAIN, with one exception: the
 * collapsed rail's identity tile, which uses the SECONDARY colour (see
 * `brandTileBg`) because that was asked for separately. No PRIMARY reaches this
 * component. The rest of the console — buttons, tabs, focus rings, the whole
 * antd surface — is still fully branded.
 */
/*
 * Sidebar background. Matches the internal admin portal's sidebar
 * (`src/admin/components/layout/Sidebar.jsx`) so both consoles read as the same
 * product. Both read `colors.sidebar` — the active brand's (theme/brands): the
 * Zenvoya violet gradient by default.
 */
const SIDEBAR_BG_GRADIENT = colors.sidebar.background;
/**
 * The gradient's TOP stop, and the ring drawn around the API-status dot.
 *
 * The dot ring used the BOTTOM stop (#140A2E), but every nav item that
 * carries a dot sits in the top third of the panel, where the gradient is still
 * `#1E0E3E`. The admin twin rings with `#1E0E3E` for exactly that reason
 * (`SidebarNavItem.jsx`, the MuiBadge-dot boxShadow).
 */
const SIDEBAR_BG_TOP = colors.sidebar.ring;
/*
 * Nav-item palette, lifted from the admin sidebar's `SidebarNavItem` so a
 * selected menu item looks identical in both consoles. These are the
 * pre-branding literals — NOT the partner's primary, see the note above.
 */
/** Active item's left accent bar. */
const SIDEBAR_ACCENT = colors.sidebar.accent;
/** Selected item's pill fill, and the hover fill for everything else. */
const SIDEBAR_ACTIVE_BG = colors.sidebar.activeBg;
const SIDEBAR_HOVER_BG = colors.sidebar.hoverBg;
/** Label/icon colours: resting, hovered, and when the item is the selected one. */
const SIDEBAR_TEXT = colors.sidebar.text;
const SIDEBAR_TEXT_HOVER = colors.sidebar.textHover;
const SIDEBAR_TEXT_ACTIVE = colors.sidebar.textActive;
const SIDEBAR_ICON_ACTIVE = colors.sidebar.iconActive;
/* The section-caption ink that used to live here is gone with the caption
   itself — admin renders no group labels, so neither does this. */
/** The version line at the foot of the panel. */
const SIDEBAR_FOOTER_TEXT = colors.sidebar.footerText;
/**
 * The two hairlines that were already here — under the logo block, and above
 * the footer. A violet tint at 15% rather than a white alpha, which on this
 * panel read as a grey scratch. NO new rules are drawn between nav groups:
 * admin does separate its groups that way, but this sidebar never did, and
 * matching admin's COLOUR was the ask - not adding its furniture.
 */
const SIDEBAR_DIVIDER = colors.sidebar.divider;
/**
 * Give a MUI icon the size its slot wants.
 *
 * The nav icons are MUI (see the note at the top of config/navigation.jsx), and
 * a MUI SvgIcon sets its own font-size rather than inheriting the one on the
 * wrapping span — so an unsized icon renders at MUI's 24px default no matter
 * what the slot says. Admin passes the size explicitly for the same reason
 * (`<Icon sx={{ fontSize: nested ? 20 : 22 }} />`), so this does the same by
 * cloning, since these icons arrive as elements from the config rather than as
 * components.
 *
 * There is no check for WHICH kit the icon comes from, deliberately — see the
 * note in the body. A plain `style` is correct for both.
 */
const sizedIcon = (icon, size) => {
  if (!isValidElement(icon)) return icon;
  /* NO `muiName` CHECK. It looks like the right guard and it never passes:
     `createSvgIcon` sets `Component.muiName` on its inner function and then
     exports `React.memo(React.forwardRef(Component))`, and the memo object does
     not carry the property. So `icon.type.muiName` is undefined for every
     @mui/icons-material icon, the guard returned early every time, and the size
     was never applied — which is why these rendered at MUI's 24px default while
     admin's were 22. A plain `style` is safe for either kit anyway: antd icons
     put it on their span, MUI icons on the svg, and both size off font-size. */
  /* And an INLINE style rather than `sx`: an `sx` rule is another class, and
     which class wins depends on stylesheet order — there is no ThemeProvider on
     this side to make that predictable. Inline beats every class. */
  return cloneElement(icon, {
    style: { fontSize: size, ...(icon.props?.style || {}) },
  });
};

/** The collapse chevron button. */
const SIDEBAR_BTN_INK = colors.sidebarChrome.icon;
const SIDEBAR_BTN_INK_HOVER = colors.sidebarChrome.iconHover;

/* ─── Single nav item ─── */
function SidebarItem({ item, active, sidebarCollapsed, onClick, indent, onNewChat }) {
  // QA-only status dot on the icon: yellow = APIs wired, awaiting sign-off;
  // green = QA verified. Undefined (nothing rendered) when SHOW_API_STATUS_BADGE
  // (flagSwitch/SwitchConfig) is off.
  const dotColor = apiStatusColor(item.apiStatus);
  /**
   * Hover as STATE, not as a DOM write.
   *
   * The handlers used to assign `currentTarget.style.backgroundColor`
   * directly, which can only reach the row itself. The admin twin also lightens
   * the LABEL and ICON on hover (`#E8D5FF`), and those are child spans with
   * their own inline colours — unreachable from the row's own style object, and
   * an inline colour would beat a CSS class anyway. Tracking it in state lets
   * all three read the same flag.
   */
  const [hovered, setHovered] = useState(false);
  /**
   * The new-chat glyph's OWN hover, separate from the row's.
   *
   * Admin expresses this as `&:hover` on the glyph inside an `sx`; this side
   * paints with inline styles, which cannot carry a pseudo-class at all, so
   * the same state has to be tracked explicitly. Without it the glyph only
   * responded to the ROW being hovered, which is the difference that showed
   * between the two portals: on admin the icon answers the pointer itself,
   * here it did not move until you were already over the whole row.
   */
  const [newChatHovered, setNewChatHovered] = useState(false);

  /** Resting → hovered → selected, matching admin's SidebarNavItem. */
  const labelInk = active ? SIDEBAR_TEXT_ACTIVE : hovered ? SIDEBAR_TEXT_HOVER : SIDEBAR_TEXT;
  const iconInk = active ? SIDEBAR_ICON_ACTIVE : hovered ? SIDEBAR_TEXT_HOVER : SIDEBAR_TEXT;

  const content = (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        /* No gap: admin's 36px ListItemIcon IS the gap. Measured against
           admin's own values, a 24px box plus a 12px gap lands the label in
           exactly the same place but centres the glyph 6px further left, which
           is what made the icons read as larger and crowded. */
        gap: 0,
        /* admin: `minHeight: nested ? 42 : 46`. The previous commit set 46 for
           both, so every child row sat four pixels taller than its twin. */
        minHeight: indent ? size[42] : size[46],
        /* admin: `mx: 1, mb: 0.5` — 8px at the sides, 4px underneath, none on
           top. The container's own gap goes, or the two would add to 6px. */
        margin: spacing(0, 8, 4),
        padding: sidebarCollapsed ? '0' : spacing(0, 16),
        paddingLeft: sidebarCollapsed ? 0 : space[16],
        borderRadius: radius.lg,
        cursor: 'pointer',
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
        position: 'relative',
        backgroundColor: active ? SIDEBAR_ACTIVE_BG : hovered ? SIDEBAR_HOVER_BG : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Active left accent bar */}
      {active && !sidebarCollapsed && (
        <div style={{
          position: 'absolute',
          left: 0,
          top: '20%',
          bottom: '20%',
          // admin: `width: 3, borderRadius: '0 3px 3px 0'`.
          width: size[3],
          borderRadius: `0 ${size[3]}px ${size[3]}px 0`,
          backgroundColor: SIDEBAR_ACCENT,
        }} />
      )}
      <span style={{
        /* Kept for any antd icon still passed through here — those DO inherit
           font-size. The nav's own icons are MUI and are sized by the clone
           below instead, because a MUI SvgIcon sets its own size and ignores
           an inherited one. */
        fontSize: indent ? typography.fontSize[20] : typography.fontSize[22],
        color: iconInk,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        /* admin's ListItemIcon is a 36px box with the glyph centred in it. */
        minWidth: size[36],
        position: 'relative',
        transition: 'color 0.15s ease',
      }}>
        {/* The dot is anchored to THIS box, which is exactly the icon — not to
            the 36px slot around it. Admin gets that for free by wrapping the
            icon in a MUI Badge; here the slot is the positioned ancestor unless
            something closer says otherwise, so once the slot grew to 36px the
            dot drifted out of the glyph and towards the label. */}
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          {sizedIcon(item.icon, indent ? size[20] : size[22])}
          {dotColor && (
            <span style={{
              position: 'absolute',
              top: -space[2],
              right: -space[4],
              width: size[8],
              height: size[8],
              borderRadius: '50%',
              backgroundColor: dotColor,
              // Ring in the sidebar colour so the dot reads as separate from the
              // glyph it overlaps, the same way the admin MUI Badge does — and in
              // the gradient's TOP stop, which is what the dots actually sit on.
              boxShadow: `0 0 0 ${borderWidth.medium} ${SIDEBAR_BG_TOP}`,
            }} />
          )}
        </span>
      </span>
      {!sidebarCollapsed && (
        <span style={{
          fontSize: typography.fontSize[14],
          /* Inter at -0.01em, as admin's SidebarNavItem sets it. Left unset,
             the label inherited the page font — DM Sans at -0.5px from
             index.css — and read smaller and tighter than admin's. */
          fontFamily: typography.fontFamily.inter,
          letterSpacing: '-0.01em',
          /* admin keeps every label at 500 and marks the active one with colour
             alone — it never bolds it. 450 is not a weight Inter ships, so it
             was rounding to 400 and reading lighter than admin's besides. */
          fontWeight: typography.fontWeight.medium,
          color: labelInk,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          /* admin tracks its labels NEGATIVE — this was +0.01em, so the same
             words were set at different widths in the two panels. */
          letterSpacing: '-0.01em',
          lineHeight: typography.lineHeight.normal,
          transition: 'color 0.15s ease',
        }}>
          {item.label}
        </span>
      )}
      {/*
        SMB-4110 — "start a FRESH conversation", beside the row's own label.

        Only on `newChat` rows (Travel → Start Chat), and never on the
        collapsed rail, where the row is the glyph and there is no space for a
        second one.

        AT REST it is styled as the group's expand arrow is — the row's own
        ink at `opacity: 0.6`, exactly what `CollapsibleHeader` gives its
        ExpandMore/ExpandLess. Taking the row's ink rather than fixing a colour
        is what makes it track the row's resting/hover/active states instead of
        sitting at one value while everything around it moves.

        ON ITS OWN HOVER it goes WHITE, which is the one thing it does not
        share with those arrows. The arrows only disclose; this destroys a
        conversation, so once the pointer is on the glyph — and not merely on
        the row — it stops blending in and says the click will land here rather
        than on the row underneath. Same treatment as the admin twin.

        `stopPropagation` because the row itself is the click target that
        navigates; without it this would fire the navigation too and race the
        state-carrying one below.
      */}
      {item.newChat && !sidebarCollapsed && (
        /* MUI's Tooltip, placement and all, rather than the native `title`
           attribute — admin's glyph carries one and the browser's own has a
           second-long delay and its own OS styling, which is exactly the kind
           of difference between the two panels this is meant to remove. */
        <Tooltip title="Start a new chat" placement="right">
          <span
            role="button"
            tabIndex={0}
            aria-label="Start a new chat"
            onClick={(event) => {
              event.stopPropagation();
              onNewChat?.();
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.stopPropagation();
              event.preventDefault();
              onNewChat?.();
            }}
            onMouseEnter={() => setNewChatHovered(true)}
            onMouseLeave={() => setNewChatHovered(false)}
            onFocus={() => setNewChatHovered(true)}
            onBlur={() => setNewChatHovered(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              /* 28, not 24: the glyph inside is 22px, and a 24px box left it
                 a single pixel each side, which the quarter-turn clipped
                 against. */
              width: size[28],
              height: size[28],
              /* `auto`, not a fixed gap: the label span here is sized to its
                 content (no flex-grow, unlike admin's ListItemText), so a
                 fixed margin would park this right after the words instead of
                 at the row's right edge where the group arrows sit. This
                 right-aligns it without changing how the label is laid out. */
              marginLeft: 'auto',
              flexShrink: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              /* The same three things admin's `&:hover` does: white ink, full
                 opacity and a quarter turn. Driven by the glyph's OWN hover,
                 so it answers the pointer before the row does. */
              color: newChatHovered ? colors.sidebar.actionHover : iconInk,
              opacity: newChatHovered ? 1 : 0.6,
              transform: newChatHovered ? 'rotate(-90deg)' : 'none',
              transition: 'opacity 0.15s ease, color 0.15s ease, transform 0.2s ease',
            }}
          >
            <RefreshIcon style={{ fontSize: typography.fontSize[22] }} />
          </span>
        </Tooltip>
      )}
    </div>
  );

  if (sidebarCollapsed) {
    return (
      <Tooltip title={item.label} placement="right">
        {content}
      </Tooltip>
    );
  }
  return content;
}

/* ─── Collapsible group header ─── */
function CollapsibleHeader({ group, expanded, hasActiveChild, sidebarCollapsed, onToggle }) {
  const [hovered, setHovered] = useState(false);

  /**
   * COLOUR follows admin's SidebarNavGroup exactly: resting `#A990C8`, hover
   * `#E8D5FF`, and no selected-state tint — admin's group header does not
   * recolour itself when one of its children is active.
   *
   * The active-child cue is kept, but only in the two channels admin does not
   * use for colour: the label's WEIGHT, and the collapsed-group fill (which is
   * already admin's own hover token, so it introduces no new colour). Dropping
   * the cue entirely would have matched admin more literally at the cost of the
   * only hint that a collapsed group contains the current page.
   */
  const ink = hovered ? SIDEBAR_TEXT_HOVER : SIDEBAR_TEXT;

  const content = (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        minHeight: size[46],
        /* IDENTICAL to a leaf row: `0 8px 4px`.
           This had 12px on top, from reading admin's `SidebarNavGroup`, whose
           collapsible header does carry `mt: 1.5`. But that branch is not what
           renders Travel or Bookings over there — admin's navigation config has
           no group labels, so every row including the expandable ones is a
           sibling inside one unlabelled group, rendered as a ListItemButton with
           `mx: 1, mb: 0.5` and NO top margin. Dashboard to Travel is therefore
           4px in admin, and the 12px here was the visible gap. */
        margin: spacing(0, 8, 4),
        padding: sidebarCollapsed ? '0' : spacing(0, 16),
        borderRadius: radius.lg,
        cursor: 'pointer',
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
        backgroundColor: hovered || (hasActiveChild && !expanded) ? SIDEBAR_HOVER_BG : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{
        fontSize: typography.fontSize[22],
        color: ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: size[36],
        transition: 'color 0.15s ease',
      }}>
        {sizedIcon(group.icon, size[22])}
      </span>
      {!sidebarCollapsed && (
        <>
          <span style={{
            flex: 1,
            fontSize: typography.fontSize[14],
            /* admin's parent items (Travel, Bookings, Reports): Inter, 500,
               -0.01em — never bolded. Left unset, the font was inherited
               DM Sans from index.css and read smaller than admin's. */
            fontFamily: typography.fontFamily.inter,
            fontWeight: typography.fontWeight.medium,
            color: ink,
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
            lineHeight: typography.lineHeight.normal,
            transition: 'color 0.15s ease',
          }}>
            {group.section}
          </span>
          {/* admin SWAPS two glyphs — ExpandLess when open, ExpandMore when
              shut — at 18px and `opacity: 0.6` in the header's own ink. It does
              not rotate a single chevron, which is what this did: a 10px
              DownOutlined turning between 0deg and -90deg, so the arrow pointed
              LEFT when collapsed where admin's points DOWN. Same two icons now,
              same size, no rotation. */}
          {expanded
            ? <ExpandLess style={{ fontSize: typography.fontSize[18], color: ink, opacity: 0.6 }} />
            : <ExpandMore style={{ fontSize: typography.fontSize[18], color: ink, opacity: 0.6 }} />}
        </>
      )}
    </div>
  );

  if (sidebarCollapsed) {
    return (
      <Tooltip title={group.section} placement="right">
        {content}
      </Tooltip>
    );
  }
  return content;
}

/* ─── Main layout ─── */
export default function AppLayout() {
  useDisableBrowserNav();
  const [collapsed, setCollapsed] = useState(false);
  const muiTheme = useTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  /** Hover for the collapse/close chevron — see its render site. */
  const [collapseHovered, setCollapseHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * 768px, stated as a raw media query on purpose.
   *
   * This was AntD's `Grid.useBreakpoint()` with `screens.md === false`, and
   * AntD's `md` is 768 where MUI's is 900. Swapping in
   * `theme.breakpoints.down('md')` would have moved the point at which this
   * console becomes a phone by 132px — every width between them would have
   * flipped to the mobile shell. The number is the behaviour here, so it is
   * written down rather than inherited.
   */
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  /**
   * The sidebar identity: the partner's uploaded logo, else their NAME.
   *
   * No lettered tile in front of either — that was tried and removed. The
   * decision landed here after several passes (logo alone, logo above the name,
   * a coloured initial tile, name alone), so the render site below spells out
   * what each branch is for.
   */
  const branding = useThemeStore((s) => s.branding);
  /**
   * THE SIGNED-IN PARTNER'S NAME COMES FIRST — from the sign-in response, not
   * from the branding.
   *
   * `branding.name` is only populated when §30.1 happens to return one, and it
   * does not today, so this fell through to the hardcoded 'Zenvoya' and every
   * partner saw Zenvoya's name over their own console. `useAuthStore.partner`
   * is the tenant that actually signed in — the same source the header used
   * before this block took over the identity.
   */
  const authPartner = useAuthStore((s) => s.partner);
  const brandName = authPartner?.name || branding?.name || 'Zenvoya';
  /**
   * THE LOGO, AND WHICH BACKGROUND IT IS DRAWN FOR.
   *
   * The wordmark rides in a white pill now, as admin's does, which inverts the
   * rule this used to follow. It preferred `logoDarkUrl` — correct while the
   * logo sat straight on the dark panel — but a dark-variant logo is light-inked
   * by definition, and on a white pill it disappears: the exact failure the dark
   * variant exists to prevent, facing the other way.
   *
   * So the PRIMARY logo leads, because that is the slot for a logo meant to go
   * on a light surface, and it gets the pill. A partner who uploaded ONLY a dark
   * variant keeps the old treatment instead — drawn straight onto the panel,
   * no pill — since pilling it would be the one guaranteed way to make it
   * invisible. `pillLogo` is what the render site reads for that.
   */
  const brandWordmark = branding?.logoUrl || branding?.logoDarkUrl || '';
  const pillLogo = !!branding?.logoUrl;
  /**
   * A LOGO URL THAT WILL NOT LOAD FALLS BACK TO THE NAME.
   *
   * A deleted upload can outlive its file — the tab's cached branding paints
   * the old URL before §30.1 answers — and the browser then draws a broken-image
   * glyph with the alt text inside the white pill. Remembering WHICH src failed,
   * rather than a plain boolean, means a new upload gets its own chance to load.
   */
  const [failedLogoSrc, setFailedLogoSrc] = useState('');
  const showLogo = !!brandWordmark && failedLogoSrc !== brandWordmark;
  /**
   * THE COLLAPSED-RAIL TILE IS FILLED WITH THE SECONDARY COLOUR.
   *
   * Product's call: the square left of the name — and, collapsed, the square
   * on its own — is the partner's secondary swatch, not the primary. The
   * primary keeps everything else, so the two brand colours stay visibly
   * distinct in the chrome instead of the secondary having no consumer at all.
   *
   * The glyph on top MUST be derived and cannot stay the hardcoded white it
   * was: the branding page permits a secondary that fails contrast, and the
   * current one (#A1F919) is 1.31:1 against white — an invisible letter —
   * while black on it is 16.08:1.
   */
  const brandTileBg = partnerBrand.secondary;
  const brandTileInk = readableTextOn(branding?.secondaryColor);

  /**
   * The sidebar this role actually gets — sections whose every item is denied
   * are dropped rather than rendered as an empty heading.
   *
   * Keyed on `perms.list` rather than `perms`: the store rebuilds the checker
   * object on every `fromSession` call, and the permission array is the only
   * thing that can change the answer.
   *
   * The three walks below (`getActivePath`, `getInitialExpanded`, and the
   * route-change effect) deliberately keep using the UNFILTERED `sidebarNav`.
   * They answer "which section should be open" and "which item is highlighted",
   * and a section that was filtered out never renders to read either flag —
   * whereas filtering them too would make the active-path fallback depend on
   * permissions, which is how you end up with no highlight at all on a screen
   * the operator is legitimately looking at.
   */
  const perms = useAuthStore((s) => s.perms);
  const visibleNav = useMemo(() => filterNavigation(sidebarNav, perms), [perms?.list]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (path) => {
    if (path === '/partnerAdmin') return location.pathname === '/partnerAdmin';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getActivePath = () => {
    // Find the most specific (longest) matching path to avoid parent routes
    // matching when a child route is active (e.g. /bookings vs /bookings/manual-hotel)
    let bestMatch = null;
    for (const group of sidebarNav) {
      for (const item of group.items) {
        if (isActive(item.path)) {
          if (!bestMatch || item.path.length > bestMatch.length) {
            bestMatch = item.path;
          }
        }
      }
    }
    if (bestMatch) return bestMatch;
    if (location.pathname.startsWith('/partnerAdmin/bookings/')) return '/partnerAdmin/bookings';
    return '/partnerAdmin';
  };

  const activePath = getActivePath();

  // Only the section holding the current route starts open, as in the admin
  // sidebar (SMB-4229) — opening every section overflowed the rail into a scrollbar.
  const getInitialExpanded = () => {
    const open = {};
    for (const group of sidebarNav) {
      if (group.collapsible && group.items.some((item) => isActive(item.path))) {
        open[group.section] = true;
      }
    }
    return open;
  };

  const [expandedSections, setExpandedSections] = useState(getInitialExpanded);

  // Update expanded state when route changes
  useEffect(() => {
    setExpandedSections((prev) => {
      const next = { ...prev };
      for (const group of sidebarNav) {
        if (group.collapsible && group.items.some((item) => isActive(item.path))) {
          next[group.section] = true;
        }
      }
      return next;
    });
    // Off-canvas sidebar shouldn't stay open across navigation on mobile.
    setMobileOpen(false);
    // `isActive` is rebuilt every render but only reads `location.pathname`,
    // which is already the dependency — listing it would rerun this each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // On mobile the sidebar is an off-canvas drawer: full width, slides in/out,
  // and ignores the desktop collapsed state (collapsing an already-hidden
  // panel serves no purpose and there's no room for a rail + overlay both).
  // Only the arrows open or close it — hovering the rail does nothing.
  const rail = collapsed && !isMobile;
  const sidebarWidth = isMobile ? SIDEBAR_WIDTH : (rail ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);

  return (
    <div
      /* `100dvh` where the browser has it, `100vh` as the fallback — see
         partnerPolish.css. A phone's `100vh` is the URL-bar-EXPANDED height, so
         the shell overshot the visible viewport and left a phantom scroll. */
      className="pa-fullheight"
      style={{
        display: 'flex',
        /**
         * THE SIDEBAR'S CURRENT WIDTH, PUBLISHED TO DESCENDANTS.
         *
         * For anything positioned `fixed` that has to line up with the panel
         * but sits too deep to know whether it is open, collapsed, or an
         * off-canvas drawer — currently the sector-selection action bar in
         * BookingDetailPage. It hardcoded `left: 240`, which was never
         * SIDEBAR_WIDTH (264) and so ran 24px under the panel at every desktop
         * width, and left a 168px dead gap once the sidebar was collapsed to
         * the 72px rail. A copy of a layout constant cannot track a layout
         * STATE; this can.
         */
        '--pa-sidebar-w': isMobile ? '0px' : `${rail ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH}px`,
      }}
    >
      {/* ─── Backdrop (mobile only, while drawer is open) ─── */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: palette.alpha.black[45],
            /* Above the header (MUI `theme.zIndex.appBar`, 1100) and one below
               the panel itself — see the panel's own zIndex for why. */
            zIndex: 1199,
          }}
        />
      )}

      {/* ─── Sidebar ─── */}
      <div
        style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        background: SIDEBAR_BG_GRADIENT,
        borderRight: colors.sidebar.edge,
        /* Square on the left (window edge), 12px kept on the right — matching
           admin's sidebar. */
        borderRadius: `0 ${radius.xl}px ${radius.xl}px 0`,
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        /**
         * ON MOBILE THE PANEL MUST OUTRANK THE HEADER.
         * `PartnerHeader` is fixed at `theme.zIndex.appBar` (1100). At 100 this
         * panel slid in UNDERNEATH it, and since the drawer is `top: 0` the
         * thing the header covered was exactly the brand block — so the
         * off-canvas menu opened with no logo, starting straight at "Dashboard".
         * Admin does not have the bug because its sidebar is a MUI
         * `variant="temporary"` Drawer, which sits at `theme.zIndex.drawer`
         * (1200) and so overlays its own header. 1200 matches that.
         * Desktop keeps 100: there the panel and the header don't overlap (the
         * content column is inset by `--pa-sidebar-w`), and any dialog/popover
         * layering that was tuned against 100 stays as it was.
         */
        zIndex: isMobile ? 1200 : 100,
        display: 'flex',
        flexDirection: 'column',
        transition: isMobile ? 'transform 0.25s ease' : 'width 0.2s ease, min-width 0.2s ease',
        transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)',
        overflowX: 'hidden',
        boxShadow: isMobile && mobileOpen ? `4px 0 24px ${palette.alpha.black[25]}` : 'none',
      }}>

        {/* Logo / Brand */}
        <div style={{
          // minHeight, NOT a fixed height: with a logo uploaded this box stacks
          // the logo above the name row, and a hard 68px clipped the second row.
          minHeight: size[68],
          display: 'flex',
          alignItems: 'center',
          // admin: `px: 2.5` (20px) beside the wordmark.
          padding: rail ? muiTheme.spacing(1.5, 1.75) : muiTheme.spacing(1.5, 2.5),
          justifyContent: rail ? 'center' : 'space-between',
          flexShrink: 0,
        }}>
          {rail ? (
            /*
              THE PARTNER'S OWN ICON WHEN THEY HAVE ONE, ELSE THE EXPAND ARROW.
              The rail's own expand control also lives in the footer now (see
              below) — always present there regardless of what's shown here —
              so this square is free to be identity FIRST and only falls back
              to acting as the toggle when there is no icon to show instead.
              An uploaded icon sits on WHITE — it's an arbitrary image, often
              with its own transparency, and needs a neutral backdrop the way
              admin's own logo tile gets one; the fallback arrow keeps the
              brand-secondary tile since there is no image colour to clash with.
            */
            <div
              onClick={branding?.iconUrl ? undefined : () => setCollapsed(false)}
              style={{
                width: size[38], height: size[38], borderRadius: radius.lg,
                backgroundColor: branding?.iconUrl ? palette.alpha.white[95] : brandTileBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: branding?.iconUrl ? 'default' : 'pointer',
              }}
            >
              {branding?.iconUrl
                ? <img src={branding.iconUrl} alt={brandName} style={{ width: size[28], height: size[28], objectFit: 'contain', borderRadius: radius.sm }} />
                : <ChevronRightIcon style={{ color: brandTileInk, fontSize: typography.fontSize[18] }} />}
            </div>
          ) : (
            <>
              {/*
                THE SIDEBAR IDENTITY — logo on top, name row underneath.
                The uploaded logo no longer REPLACES the name; it sits above it,
                and the name row (initial tile + partner name) is always present.
                So a partner with a logo gets logo + name, and one without gets
                just the name row — the same block, one piece missing.
                ── THE TILE IS THE PARTNER'S INITIAL, NOT THE UPLOADED ICON ──
                It used to prefer the square "Icon Only" upload. Product decision
                to drop that: the Icon Only asset stands in for the mascot across
                the traveller app, and reusing it here made the console look like
                it was branded by an avatar. To bring it back, read
                `branding.iconUrl` and render it in place of this tile.
              */}
              {/*
                THE UPLOADED LOGO, ELSE THE PARTNER'S NAME — left-aligned, one or
                the other, never both. An uploaded logo IS the partner's
                identity, so it stands alone; the name is the fallback for a
                partner who has not uploaded one. There is no lettered tile in
                front of either.
                THE LOGO RIDES IN A WHITE PILL, as admin's own wordmark does —
                same near-white fill, same 10px radius, same left edge. It is
                what lets an arbitrary uploaded logo sit on this dark panel and
                still be legible whatever ink it was drawn in, rather than
                depending on the partner having uploaded a dark variant. See
                `brandWordmark` above for why that also flips which variant is
                preferred.
                The NAME fallback keeps no pill: it is text this panel draws
                itself, in its own white ink, and boxing it would invent a
                treatment admin has no equivalent for.
                `flex: 1` so whichever renders takes the space left of the
                collapse button rather than sitting hard against it.
              */}
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                /* Logo and name both sit LEFT, against the edge the nav items
                   below start from — product decision; the name used to centre. */
                justifyContent: 'flex-start',
                minWidth: 0,
              }}>
                {showLogo ? (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    /* admin's pill exactly: 5px ends, 12px sides, 10px radius.
                       6px (the even-number rule) made it visibly taller than
                       admin's; matching admin wins here. */
                    ...(pillLogo ? {
                      padding: spacing(5, 12),
                      borderRadius: radius.lg,
                      backgroundColor: palette.alpha.white[95],
                    } : {}),
                    maxWidth: '100%',
                    minWidth: 0,
                  }}>
                    <img
                      /* NOT lazy — this is the first thing on every screen, and
                          deferring it trades a download for a blank pill. It is
                          decoded off the main thread and asked for ahead of the
                          page's other images instead. No cache-busting needed:
                          app-backend now writes each upload under a unique,
                          timestamped key with a long, immutable Cache-Control,
                          so this URL never goes stale and never needs one. */
                      decoding="async"
                      fetchpriority="high"
                      src={brandWordmark}
                      alt={brandName}
                      onError={() => setFailedLogoSrc(brandWordmark)}
                      /* 26px in the pill to match the height admin gives its own
                         wordmark, so the pill comes out the same size on both
                         consoles; the un-pilled variant keeps its old 34px, with
                         the whole panel width to breathe into. */
                      style={{ display: 'block', maxHeight: pillLogo ? size[26] : size[34], maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <div style={{
                    color: colors.sidebar.brandText, fontWeight: typography.fontWeight.semiBold, fontSize: typography.fontSize[15], lineHeight: typography.lineHeight.snug,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    textAlign: 'left',
                  }}>
                    {brandName}
                  </div>
                )}
                {/* "PARTNER PORTAL" subtitle — commented out per product. The
                    partner's own name is the identity that matters here, and
                    the label added a second line of chrome to every screen.
                <div style={{
                  color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 500,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  Partner Portal
                </div>
                */}
              </div>
              {/* Collapse / close. admin's is a 28px MUI IconButton, which is
                  CIRCULAR by default — this was a 30px rounded square. It also
                  carries a resting violet fill rather than nothing and lightens
                  both fill and glyph on hover, so both channels move here too,
                  and the chevron is admin's own ChevronLeft at 18px rather than
                  antd's 12px LeftOutlined. */}
              <div
                onClick={() => (isMobile ? setMobileOpen(false) : setCollapsed(true))}
                style={{
                  width: size[28], height: size[28], borderRadius: radius.circle,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  color: collapseHovered ? SIDEBAR_BTN_INK_HOVER : SIDEBAR_BTN_INK,
                  backgroundColor: collapseHovered ? SIDEBAR_ACTIVE_BG : SIDEBAR_HOVER_BG,
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={() => setCollapseHovered(true)}
                onMouseLeave={() => setCollapseHovered(false)}
              >
                {isMobile
                  ? <CloseIcon sx={{ fontSize: typography.fontSize[14] }} />
                  : <ChevronLeftIcon sx={{ fontSize: (t) => t.spacing(2.25) }} />}
              </div>
            </>
          )}
        </div>
        {/* The rule under the logo, inset 16px each side like admin's
            `<Divider sx={{ mx: 2 }} />` rather than running edge to edge. */}
        <div style={{ height: borderWidth.thin, backgroundColor: SIDEBAR_DIVIDER, margin: spacing(0, 16), flexShrink: 0 }} />

        {/* Navigation */}
        <div className="pa-sidebar-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: spacing(12, 0) }}>
          {visibleNav.map((group, groupIdx) => {
            const hasActiveChild = group.items.some((item) => activePath === item.path);

            if (group.collapsible) {
              const isExpanded = !!expandedSections[group.section];

              return (
                <div key={group.section}>
                  <CollapsibleHeader
                    group={group}
                    expanded={isExpanded}
                    hasActiveChild={hasActiveChild}
                    sidebarCollapsed={rail}
                    onToggle={() => toggleSection(group.section)}
                  />
                  {/* Children — animated expand/collapse.
                      The 12px indent lives HERE, not on the rows, because that
                      is where admin puts it: a `pl: 1.5` List wrapping the
                      nested items, each row keeping the same px as a top-level
                      one. Indenting the row instead left the pill full width
                      with its contents pushed in, and at 44px of padding the
                      icons sat 28px from their parent's, where admin's sit
                      12px. */}
                  <div style={{
                    overflow: 'hidden',
                    paddingLeft: rail ? 0 : 12,
                    maxHeight: (isExpanded || rail) ? group.items.length * 48 : 0,
                    opacity: (isExpanded || rail) ? 1 : 0,
                    transition: 'max-height 0.25s ease, opacity 0.15s ease',
                  }}>
                    {group.items.map((item) => (
                      <SidebarItem
                        key={item.key}
                        item={item}
                        active={activePath === item.path}
                        sidebarCollapsed={rail}
                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                        /* SMB-4110. The nonce is what makes this work when the
                           operator is ALREADY on Start Chat: same path, new
                           location state, so the travel page sees a change it
                           can act on. See components/travelApp/newChatSignal.js. */
                        onNewChat={() => {
                          navigate(item.path, { state: newChatNavState() });
                          setMobileOpen(false);
                        }}
                        indent={!rail}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            // Non-collapsible section (Overview)
            return (
              <div key={group.section}>
                {/* NO CAPTION, and no rule either — admin renders neither.
                    Its navigation config carries no group labels at all, so a
                    non-collapsible group is just its items: `SidebarNavGroup`
                    returns the children directly and the list starts straight
                    under the header divider. "OVERVIEW" was the last thing
                    making the two panels read differently, so it goes.
                    `group.section` is still the React key and still names the
                    group for the collapsible branch above; only this caption is
                    dropped. */}
                {/* A plain block, NOT a flex column. Measured against admin,
                    Dashboard→Travel was 16px here against admin's 12. Admin
                    stacks its items in a block Box, so the last item's 4px
                    bottom margin COLLAPSES into the group's own — a flex
                    container never collapses its children's margins, so that
                    4px was being added instead of absorbed. The items each
                    carry their own margin now, so there is nothing for flex to
                    do here anyway. */}
                <div>
                  {group.items.map((item) => (
                    <SidebarItem
                      key={item.key}
                      item={item}
                      active={activePath === item.path}
                      sidebarCollapsed={rail}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      indent={false}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — always present, matching admin's split: the version line
            when expanded, the rail's own expand control when collapsed. That
            control used to live on the logo tile above; admin keeps the two
            apart (logo up top, chevron down here), so this is where it moved. */}
        <div style={{
          padding: rail ? muiTheme.spacing(1.25) : muiTheme.spacing(1.75, 2.75),
          borderTop: `${borderWidth.thin} solid ${SIDEBAR_DIVIDER}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}>
          {rail ? (
            <div
              onClick={() => setCollapsed(false)}
              style={{
                width: size[28], height: size[28], borderRadius: radius.circle,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                color: collapseHovered ? SIDEBAR_BTN_INK_HOVER : SIDEBAR_BTN_INK,
                backgroundColor: collapseHovered ? SIDEBAR_ACTIVE_BG : SIDEBAR_HOVER_BG,
                transition: 'background-color 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={() => setCollapseHovered(true)}
              onMouseLeave={() => setCollapseHovered(false)}
            >
              <ChevronRightIcon style={{ fontSize: typography.fontSize[18] }} />
            </div>
          ) : (
            /* admin has no version line, so there is no colour to copy — but a
               white alpha was the last thing on this panel not drawn from the
               violet ramp, and it read as grey. `#6B5087` is admin's own muted
               footer ink. */
            <div style={{ color: SIDEBAR_FOOTER_TEXT, fontSize: typography.fontSize[14], fontFamily: typography.fontFamily.inter, width: '100%' }}>
              Partner MVP v0.1
            </div>
          )}
        </div>
      </div>

      {/* ─── Main content ─── */}
      <div
        className="pa-fullheight"
        style={{
          flex: 1,
          // A flex child's default min-width is the width of its widest
          // unwrapped content, not 0 — without this, one over-wide element
          // anywhere in a page pushes the whole app wider than the viewport
          // instead of scrolling/clipping inside its own container.
          minWidth: 0,
          /**
           * `clip`, NOT `hidden` — same clipping, without the side effect.
           *
           * `overflow-x: hidden` alongside `overflow-y: visible` computes
           * overflow-y to `auto`, which makes this element a SCROLL CONTAINER.
           * A `position: sticky` descendant then resolves its offsets against
           * THIS box instead of the viewport, and since this box is
           * `height: auto` it never scrolls — so the sticky header this very
           * element contains had nothing to stick to.
           *
           * `overflow-x: clip` clips the overflow without establishing a
           * scrollport, so `overflow-y` stays `visible` and sticky positioning
           * keeps working. Relocating the `hidden` further down was tried
           * first and only moved the problem — it would have broken the
           * in-page sticky column on the Branding screen instead.
           */
          overflowX: 'clip',
          marginLeft: isMobile ? 0 : (rail ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH),
          transition: 'margin-left 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: palette.custom.blue[30],
        }}
      >
        <PartnerHeader isMobile={isMobile} onMenuClick={() => setMobileOpen(true)} />
        {/* The Travel surfaces are a full-bleed iframe: the embedded app brings
            its own layout, so the shell's inset only framed it in dead space.
            Dropped on all sides for those routes. It has to happen HERE — the
            padding belongs to this wrapper, and a descendant cannot cancel an
            ancestor's padding. */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? space[12] : space[24],
            ...(location.pathname.startsWith('/partnerAdmin/travel') ? { padding: 0 } : null),
            minWidth: 0,
          }}
        >
          <Suspense fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: size[400] }}>
              {/* The portal's own ring: brand-coloured, and it stays invisible
                  for its first 200ms so a chunk that loads quickly does not
                  flash a spinner at the operator. */}
              <PartnerSpinner size={size[36]} />
            </div>
          }>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
